import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { FOLIO_FORMAT, INVOICE_DATE_AND_TIME_FORMAT } from "../../utils/helper";

const useFolio = (
  setCombinedInvoices,
  setRoomDetails,
  posNames,
  setPosNames,
  filteredInvoices,
  setFilteredInvoices,
  posData,
  filter
) => {
  const {
    responseDetails,
    paymentDetails: updatedPaymentDetails,
    invoiceDetails: updatedInvoiceDetails,
  } = useSelector((store) => store.booking);

  const { response } = useSelector((store) => store.rooms);

  const invoiceAmount = useMemo(() => {
    return (
      updatedInvoiceDetails?.reduce(
        (acc, curr) => acc + (curr?.total || 0),
        0
      ) || 0
    );
  }, [updatedInvoiceDetails]);

  const totalRefund = useMemo(() => {
    return (
      updatedPaymentDetails?.reduce(
        (acc, curr) => (curr.amount < 0 ? acc - curr.amount : acc),
        0
      ) || 0
    );
  }, [updatedPaymentDetails]);

  const paymentReceived = useMemo(() => {
    return (
      updatedPaymentDetails?.reduce(
        (acc, curr) => (curr.amount > 0 ? acc + curr.amount : acc),
        0
      ) || 0
    );
  }, [updatedPaymentDetails]);

  const dueAmount = useMemo(() => {
    return invoiceAmount - paymentReceived + totalRefund;
  }, [invoiceAmount, paymentReceived, totalRefund]);

  useEffect(() => {
    if (Object.keys(responseDetails).length > 0) {
      const combinedArray = Array.isArray(filteredInvoices)
        ? [...filteredInvoices]
        : [];

      combinedArray.sort((a, b) => {
        const dateA = a.createdAt || a.updatedAt || a.invoiceBilledDate || 0;
        const dateB = b.createdAt || a.updatedAt || b.invoiceBilledDate || 0;
        return dateA - dateB;
      });

      let dueAmount = 0;

      const arr = combinedArray.map((item) => {
        let newObj = {
          debit: 0,
          credit: 0,
          dueAmount: 0,
        };

        if (item.hasOwnProperty("cgst") || item?.amount < 0) {
          newObj.debit = Boolean(item.total) ? item?.total : -item.amount;
          newObj.credit = 0;
          newObj.dueAmount = Boolean(item?.total)
            ? dueAmount + item.total
            : dueAmount + -item.amount;
        } else {
          newObj.credit = item.amount;
          newObj.debit = 0;
          newObj.dueAmount = dueAmount - item.amount;
        }

        dueAmount = newObj.dueAmount; // Update dueAmount for the next iteration
        return newObj;
      });

      const updatedRoomDetails = response.find(
        (room) => room.id === responseDetails?.bookingDetails?.rooms?.[0]
      );

      let requiredCombinedArray = combinedArray?.map((Obj, idx) => {
        return {
          date: Boolean(Obj?.createdAt)
            ? INVOICE_DATE_AND_TIME_FORMAT(Obj.createdAt).formattedDate
            : INVOICE_DATE_AND_TIME_FORMAT(Obj.createdAt).formattedDate,

          time: Boolean(Obj?.createdAt)
            ? INVOICE_DATE_AND_TIME_FORMAT(Obj.createdAt).formattedTime
            : INVOICE_DATE_AND_TIME_FORMAT(Obj.createdAt).formattedTime,

          type: Boolean(Obj.hasOwnProperty("amount")) ? "payment" : "invoice",

          amount: Boolean(Obj?.amount) ? Obj?.amount : "",

          paymentPartRecordList: Boolean(Obj?.amount)
            ? Obj?.paymentPartRecordList
            : null,

          transactionNo: Boolean(Obj?.amount) ? Obj?.transactionNo : null,

          method: Boolean(Obj?.amount) ? Obj?.method : null,

          id: Boolean(Obj.hasOwnProperty("amount"))
            ? Obj?.paymentId
            : Boolean(Obj?.invoiceId)
            ? Obj?.invoiceId
            : "Tentative",

          invoiceRefrenceId: Boolean(Obj?.amount) ? null : Obj?.id,

          description: Boolean(Obj?.invoiceType)
            ? Obj?.invoiceType === "roomBill"
              ? `Room Bill`
              : posNames[Obj.posId]
            : Boolean(Obj?.amount < 0)
            ? "Refund"
            : `${Obj?.method?.toUpperCase()}  ${
                Boolean(Obj?.transactionNo) ? `(${Obj?.transactionNo})` : ""
              } `,

          detailedDescription: Boolean(Obj?.invoiceType)
            ? Obj?.priceBreakup?.[0]?.type
            : "",

          debit: Boolean(arr[idx].debit) ? FOLIO_FORMAT(arr[idx].debit) : "NA",
          credit: Boolean(arr[idx].credit)
            ? FOLIO_FORMAT(arr[idx].credit)
            : "NA",
          due: Boolean(arr[idx]?.dueAmount || arr[idx]?.dueAmount == 0)
            ? FOLIO_FORMAT(arr[idx]?.dueAmount)
            : "NA",
        };
      });

      setRoomDetails(updatedRoomDetails);
      setCombinedInvoices(requiredCombinedArray);
    }
  }, [responseDetails, response, filteredInvoices, updatedPaymentDetails]);

  useEffect(() => {
    if (posData) {
      const obj = {};
      posData?.forEach((pos) => (obj[pos.id] = pos.type));
      setPosNames(obj);
    }
  }, [posData]);

  useEffect(() => {
    if (updatedInvoiceDetails && updatedInvoiceDetails?.length) {
      if (filter === "all") {
        setFilteredInvoices([
          ...updatedInvoiceDetails,
          ...updatedPaymentDetails,
        ]);
      } else if (filter === "roomBill") {
        const updatedFilteredInvoices = [...updatedInvoiceDetails.filter(
          (invoice) => invoice?.invoiceType === "roomBill"
        )];
        setFilteredInvoices(updatedFilteredInvoices);
      } else {
        let updatedFilteredInvoices = updatedInvoiceDetails.filter(
          (invoice) => invoice?.invoiceType !== "roomBill"
        );
        const invoiceIds = updatedFilteredInvoices?.map(inv => inv?.id);
        if(invoiceIds && invoiceIds?.length > 0){
          updatedFilteredInvoices = [...updatedFilteredInvoices, 
            ...(updatedPaymentDetails?.filter(p=>invoiceIds.includes(p.invoiceId)))]
        }
        setFilteredInvoices(updatedFilteredInvoices);
      }
    }
  }, [filter, responseDetails, updatedInvoiceDetails, updatedPaymentDetails]);

  const roomInvoice = Array.isArray(updatedInvoiceDetails)
    ? updatedInvoiceDetails.find((Obj) => Obj?.invoiceType === "roomBill")
    : {};

  return { invoiceAmount, paymentReceived, dueAmount, roomInvoice };
};

export default useFolio;
